/**
 * Heal step for course-pointer deploys (referenced-lesson-packages).
 *
 * A course pointer references each contained lesson's hosted package. Lessons
 * published by the pre-pointer cascade never got standalone packages, and
 * accounts assigned during the embedded era never received per-lesson copies —
 * so before a pointer ships, every (lesson, deploy scope) pair is
 * existence-checked and any missing package is built and deployed first,
 * keeping the lessons-before-pointer ordering.
 *
 * Safety rules (the pointer model's "pending edits stay private" guarantee):
 * a lesson package is only ever built from live rows when those rows are
 * provably the published content — base lifecycle `locked`, and for an account
 * scope additionally no mid-edit client customization. Anything else refuses
 * the heal for the affected scopes, and refused/failed scopes are BLOCKED from
 * receiving the pointer at all (fail closed — a hosted pointer must never
 * reference a package that isn't there, and a heal must never deploy content
 * that isn't published).
 */
import type { PublishState } from '@course-authoring/shared-types';
import type { DeployAccountInput, DeployAccountResult } from './courseDeployApi';
import { PublishApiError } from './publishApi';
import { fetchScormExportContext } from './accountAssignmentApi';

/**
 * Course targets supply receiving scopes only. A referenced lesson may not be
 * directly assigned there, so missing context membership must not drop a scope.
 */
export async function prepareLessonHealTargets(
  lessonId: string,
  targets: readonly DeployAccountInput[],
): Promise<DeployAccountInput[]> {
  const context = await fetchScormExportContext('lesson', lessonId);
  const contentId = Number(context.contentId);
  if (context.contentType !== 'microlessons' || !Number.isSafeInteger(contentId) || contentId <= 0 || !context.host.trim()) {
    throw new Error('A valid standalone lesson SCORM identity could not be resolved.');
  }
  if (context.hasPendingEdits) {
    throw new Error('This lesson has unpublished edits; its package was not built.');
  }
  return targets.map(target => {
    const lessonAccount = context.accounts.find(account =>
      Number(account.externalId) === target.externalAccountId && account.subdomain === target.accountSlug);
    return {
      ...target,
      // Unknown membership can still carry account customization through the
      // course. Build account-resolved content instead of assuming base bytes.
      hasCustomizations: !target.base && (lessonAccount?.hasCustomizations ?? true),
      scormRuntimeConfig: {
        mode: 'emtrain',
        host: context.host,
        subdomain: target.accountSlug,
        contentId: context.contentId,
        contentType: 'microlessons',
        enforceSequence: false,
        ...(context.apiBaseUrl ? { apiBaseUrl: context.apiBaseUrl } : {}),
      },
    };
  });
}

export interface HealDeps {
  /** `fetchExportPackageInfo` — throws PublishApiError `NOT_DEPLOYED` when the scope has no hosted package. */
  fetchPackageInfo: (packageId: string, accountSlug?: string) => Promise<unknown>;
  /** Context-aware lesson lifecycle: base context when `accountId` is null. */
  fetchLessonPublishState: (lessonId: string, accountId: number | null) => Promise<PublishState>;
  /**
   * Deploys one lesson package to the given scopes, returning per-scope
   * results. `contentPublishedAt` is the lesson's base lastPublishedAt — the
   * content revision identity the deploy stamps (heal only ever builds from
   * provably-published rows, so the base publish time IS the built revision).
   */
  deployLesson: (
    lessonId: string,
    accounts: DeployAccountInput[],
    contentPublishedAt?: string,
  ) => Promise<DeployAccountResult[]>;
  /** Display name for result rows; defaults to the id. */
  describeLesson?: (lessonId: string) => string;
}

export interface HealOutcome {
  /** Per-(lesson, scope) rows for the deploy-results modal. */
  results: HealDeployAccountResult[];
  /** Scopes (by accountSlug) that must NOT receive the course pointer. */
  blockedSlugs: Set<string>;
}

export interface HealDeployAccountResult extends DeployAccountResult {
  /** Stable package identity for consumers that need to group results by content. */
  contentPublicId: string;
  contentType: 'lesson';
  /** Raw account scope; `accountSlug` may carry display text. */
  scopeSlug: string;
}

const scopeSlugFor = (target: DeployAccountInput): string | undefined =>
  target.base ? undefined : target.accountSlug;

const accountIdFor = (target: DeployAccountInput): number | null =>
  target.base ? null : target.externalAccountId ?? null;

export async function healReferencedLessonPackages(
  refIds: readonly string[],
  targets: readonly DeployAccountInput[],
  deps: HealDeps,
): Promise<HealOutcome> {
  const results: HealDeployAccountResult[] = [];
  const blockedSlugs = new Set<string>();
  const describe = deps.describeLesson ?? ((lessonId: string) => lessonId);

  const refuse = (target: DeployAccountInput, lessonId: string, error: string) => {
    blockedSlugs.add(target.accountSlug);
    results.push({
      // Display text for the results modal; scopeSlug preserves the RAW scope
      // so downstream consumers (durable failure embedding) can match rows to
      // targets — matching on the display string silently finds nothing.
      accountSlug: `${target.accountSlug} — ${describe(lessonId)}`,
      scopeSlug: target.accountSlug,
      contentPublicId: lessonId,
      contentType: 'lesson',
      success: false,
      error,
    });
  };

  for (const lessonId of refIds) {
    // ── Which scopes are missing this lesson's package? ──────────────────────
    const missing: DeployAccountInput[] = [];
    await Promise.all(
      targets.map(async (target) => {
        try {
          await deps.fetchPackageInfo(lessonId, scopeSlugFor(target));
        } catch (error) {
          if (error instanceof PublishApiError && error.code === 'NOT_DEPLOYED') {
            missing.push(target);
          } else {
            // Can't verify the package exists — don't ship a pointer that may
            // reference a hole.
            refuse(
              target,
              lessonId,
              `Could not verify this lesson's hosted package: ${error instanceof Error ? error.message : 'unknown error'}`,
            );
          }
        }
      }),
    );
    if (missing.length === 0) continue;

    // ── Is the lesson safe to build from live rows? (base lifecycle) ─────────
    let baseState: PublishState;
    try {
      baseState = await deps.fetchLessonPublishState(lessonId, null);
    } catch (error) {
      // Unreachable base lifecycle (e.g. an account-scope lesson): nothing
      // published to build from — refuse every missing scope.
      for (const target of missing) {
        refuse(
          target,
          lessonId,
          `This lesson's published state could not be confirmed (${error instanceof Error ? error.message : 'unknown error'}); its package was not built.`,
        );
      }
      continue;
    }
    if (baseState.state !== 'locked') {
      const reason =
        baseState.state === 'editing'
          ? 'This lesson has unpublished edits — publish the lesson update first, then publish the course update again.'
          : 'This lesson has never been published — publish it first, then publish the course update again.';
      for (const target of missing) refuse(target, lessonId, reason);
      continue;
    }

    // ── Account scopes must not be mid-client-edit either ────────────────────
    // Each eligible target carries its EFFECTIVE revision time: an account
    // that client-published its customization after the base publish is built
    // account-resolved, so stamping it with the base time would immediately
    // read as stale-revision. Targets are grouped by matching revision so
    // each deploy stamps the time of the content it actually ships.
    const eligible: DeployAccountInput[] = [];
    const effectivePublishedAt = new Map<string, string | undefined>();
    for (const target of missing) {
      const accountId = accountIdFor(target);
      if (accountId === null) {
        eligible.push(target);
        effectivePublishedAt.set(target.accountSlug, baseState.lastPublishedAt ?? undefined);
        continue;
      }
      try {
        const accountState = await deps.fetchLessonPublishState(lessonId, accountId);
        if (accountState.state === 'editing') {
          refuse(
            target,
            lessonId,
            'This account has unpublished lesson customizations — publish them in the client context first.',
          );
        } else {
          eligible.push(target);
          // Delivery revision is max(base, account): the account-resolved
          // build CONTAINS the newest base content, so an account whose own
          // publish predates a later base publish must stamp the base time —
          // stamping the older account time would classify the fresh package
          // stale-revision on arrival.
          const accountMs = accountState.lastPublishedAt ? Date.parse(accountState.lastPublishedAt) : NaN;
          const baseMs = baseState.lastPublishedAt ? Date.parse(baseState.lastPublishedAt) : NaN;
          const effective = Number.isFinite(accountMs) && Number.isFinite(baseMs)
            ? (accountMs >= baseMs ? accountState.lastPublishedAt : baseState.lastPublishedAt)
            : accountState.lastPublishedAt ?? baseState.lastPublishedAt;
          effectivePublishedAt.set(target.accountSlug, effective ?? undefined);
        }
      } catch (error) {
        refuse(
          target,
          lessonId,
          `This account's lesson state could not be confirmed (${error instanceof Error ? error.message : 'unknown error'}); its package was not built.`,
        );
      }
    }
    if (eligible.length === 0) continue;

    // ── Build + deploy to exactly the scopes that are missing it ─────────────
    const revisionGroups = new Map<string, DeployAccountInput[]>();
    for (const target of eligible) {
      const key = effectivePublishedAt.get(target.accountSlug) ?? '';
      const group = revisionGroups.get(key);
      if (group) {
        group.push(target);
      } else {
        revisionGroups.set(key, [target]);
      }
    }
    for (const [revision, groupTargets] of revisionGroups) {
      const deployResults = await deps.deployLesson(lessonId, groupTargets, revision || undefined);
      for (const result of deployResults) {
        if (!result.success) blockedSlugs.add(result.accountSlug);
        // Same display-vs-raw split as refuse(): downstream durable-detail
        // matching needs the raw scope; the modal shows the labelled form.
        results.push({
          ...result,
          accountSlug: `${result.accountSlug} — ${describe(lessonId)}`,
          scopeSlug: result.scopeSlug ?? result.accountSlug,
          contentPublicId: lessonId,
          contentType: 'lesson',
        });
      }
    }
  }

  return { results, blockedSlugs };
}
